
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Button, Divider, Chip, CircularProgress, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MaterialServiceSelectorModal from '../components/modals/MaterialServiceSelectorModal';
import CustomerFormModal from '../components/modals/CustomerFormModal';
import { createCustomer } from '../services/customerService';
import { logDeviceAction } from '../modules/devices';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PageHeader from '../components/common/PageHeader';
import { Loading } from '../components/common/Loading';
import { db } from '../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../utils/deviceDedup';

// New Modular Components & Hook
import {
    useSaleForm,
    SaleProductTable,
    SaleFormSummary
} from '../modules/sales';
import NewDeviceFields from '../modules/devices/components/NewDeviceFields';
import { useDeviceBrands } from '../modules/devices/hooks/useDeviceBrands';
import GlassCard from '../ui/glass/components/GlassCard';

const SatisOlustur = () => {
    const { id } = useParams();

    const {
        isEditMode,
        loading,
        isSaving,
        customers,
        selectedCustomerId,
        setSelectedCustomerId,
        satilanUrunler,
        toplamTutar,
        odenenTutar,
        setOdenenTutar,
        odemeTipi,
        setOdemeTipi,
        satisNotlari,
        setSatisNotlari,
        handleProductSelect,
        handleRemoveProduct,
        handleMiktarChange,
        handleFiyatChange,
        handleSubmit,
        // Cihaz Props
        isDeviceSale, setIsDeviceSale,
        seriNumarasi, setSeriNumarasi,
        marka, setMarka,
        model, setModel,
        cihazSinifi, setCihazSinifi,
        isSgk, setIsSgk,
        zimmetTarihi, setZimmetTarihi,
        faturaNo, setFaturaNo,
        verilisTarihi, setVerilisTarihi,
        garantiBaslangic, setGarantiBaslangic,
        garantiBitis, setGarantiBitis,
        periyodikBakimTarihi, setPeriyodikBakimTarihi
    } = useSaleForm(id);

    const { brandsFromDb } = useDeviceBrands();

    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

    // Yeni müşteri ekleme handler'ı
    const handleAddNewCustomer = async (customerData, deviceInfo) => {
        try {
            // Mükerrer cihaz denetimi (normalize edilmiş) — müşteri oluşturulmadan önce.
            if (deviceInfo) {
                const duplicate = await findDuplicateDeviceBySerial(deviceInfo.seriNumarasi);
                if (duplicate) {
                    alert(duplicateDeviceMessage(duplicate, deviceInfo.seriNumarasi));
                    return;
                }
            }

            const customerId = await createCustomer(customerData);

            // Cihaz bilgisi varsa ekle
            if (deviceInfo) {
                const deviceData = {
                    ...deviceInfo,
                    musteriId: customerId,
                    musteriAdSoyad: customerData.adSoyad,
                    createdAt: Timestamp.now()
                };
                const deviceRef = await addDoc(collection(db, 'devices'), deviceData);

                // Log Device Action
                await logDeviceAction(
                    deviceRef.id,
                    'create',
                    'Yeni cihaz oluşturuldu (Satış sırasında müşteri oluşturulurken)',
                    'system',
                    deviceData
                );
            }

            // Yeni oluşturulan müşteriyi otomatik seç
            setSelectedCustomerId(customerId);
            setIsCustomerModalOpen(false);
            alert('Müşteri başarıyla eklendi ve seçildi!');
        } catch (error) {
            console.error('Müşteri eklenirken hata:', error);
            alert('Müşteri eklenirken bir hata oluştu.');
        }
    };

    if (loading) return <Loading message="Yükleniyor..." />;

    return (

        <Box sx={{ p: { xs: 1, md: 2 } }}>
            {/* Header */}
            <PageHeader
                title={isEditMode ? 'Satış Kaydını Düzenle' : 'Yeni Ürün Satışı'}
                subtitle={isEditMode ? 'Mevcut satış kaydını güncelleyin.' : 'Sisteme yeni bir satış kaydı ekleyin.'}
                icon={<ShoppingCartIcon />}
            />

            {/* Müşteri Seçimi */}
            <GlassCard sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>Müşteri Bilgileri</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={9}>
                        <FormControl fullWidth>
                            <InputLabel>Müşteri *</InputLabel>
                            <Select
                                value={selectedCustomerId}
                                label="Müşteri *"
                                onChange={(e) => setSelectedCustomerId(e.target.value)}
                            >
                                {customers.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.adSoyad}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<PersonAddIcon />}
                            onClick={() => setIsCustomerModalOpen(true)}
                            sx={{ height: '56px' }}
                        >
                            Yeni Müşteri
                        </Button>
                    </Grid>
                </Grid>
            </GlassCard>

            {/* Cihaz Tanımlama (Opsiyonel) */}
            <GlassCard sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                    Cihaz Zimmetleme / Garanti Başlatma
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Eğer bu satış bir cihaz içeriyorsa, cihazı müşteriye zimmetlemek ve garantiyi başlatmak için aşağıdaki seçeneği işaretleyin.
                </Typography>
                <NewDeviceFields
                    addDevice={isDeviceSale}
                    setAddDevice={setIsDeviceSale}
                    seriNumarasi={seriNumarasi}
                    setSeriNumarasi={setSeriNumarasi}
                    marka={marka}
                    setMarka={setMarka}
                    model={model}
                    setModel={setModel}
                    cihazSinifi={cihazSinifi}
                    setCihazSinifi={setCihazSinifi}
                    isSgk={isSgk}
                    setIsSgk={setIsSgk}
                    zimmetTarihi={zimmetTarihi}
                    setZimmetTarihi={setZimmetTarihi}
                    faturaNo={faturaNo}
                    setFaturaNo={setFaturaNo}
                    verilisTarihi={verilisTarihi}
                    setVerilisTarihi={setVerilisTarihi}
                    garantiBaslangic={garantiBaslangic}
                    setGarantiBaslangic={setGarantiBaslangic}
                    garantiBitis={garantiBitis}
                    setGarantiBitis={setGarantiBitis}
                    periyodikBakimTarihi={periyodikBakimTarihi}
                    setPeriyodikBakimTarihi={setPeriyodikBakimTarihi}
                    brandsFromDb={brandsFromDb}
                />
            </GlassCard>

            {/* Satılan Ürünler */}
            <GlassCard sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>Satılan Ürünler</Typography>
                    <Button
                        onClick={() => setIsMaterialModalOpen(true)}
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                    >
                        Ürün Ekle
                    </Button>
                </Box>

                <SaleProductTable
                    satilanUrunler={satilanUrunler}
                    onMiktarChange={handleMiktarChange}
                    onFiyatChange={handleFiyatChange}
                    onRemoveProduct={handleRemoveProduct}
                />
            </GlassCard>

            {/* Ödeme ve Notlar */}
            <GlassCard sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>Ödeme ve Notlar</Typography>
                <SaleFormSummary
                    satisNotlari={satisNotlari}
                    setSatisNotlari={setSatisNotlari}
                    toplamTutar={toplamTutar}
                    odenenTutar={odenenTutar}
                    setOdenenTutar={setOdenenTutar}
                    odemeTipi={odemeTipi}
                    setOdemeTipi={setOdemeTipi}
                />

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={handleSubmit}
                        disabled={isSaving}
                        sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
                    >
                        {isSaving ? (
                            <>
                                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                Kaydediliyor...
                            </>
                        ) : (
                            isEditMode ? 'Değişiklikleri Kaydet' : 'Satışı Tamamla'
                        )}
                    </Button>
                </Box>
            </GlassCard>

            <MaterialServiceSelectorModal
                isOpen={isMaterialModalOpen}
                onClose={() => setIsMaterialModalOpen(false)}
                onSelect={handleProductSelect}
            />

            <CustomerFormModal
                isOpen={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                onAddCustomer={handleAddNewCustomer}
                onUpdateCustomer={() => { }} // Boş, çünkü sadece yeni müşteri ekleniyor
                customerToEdit={null}
            />
        </Box>
    );

};

export default SatisOlustur;
